import type { Duration } from 'date-fns';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';
import styled from 'styled-components';

export const TabNum = styled.span`
  font-variant-numeric: tabular-nums;
`;

type TimeElements = {
  months: number;
  days: number;
  hours: number;
  minutes: string;
  seconds: string;
  rawMinutes: number;
};

type TimeMessages = {
  monthsLeft: MessageDescriptor;
  daysLeft: MessageDescriptor;
  hoursLeft: MessageDescriptor;
  minutesLeft: MessageDescriptor;
  secondsLeft: MessageDescriptor;
};

export type TimeOptions = {
  /**
   * Instead of returning number of months, aggregate the total number of days.
   */
  customMessages?: typeof defaultTimeMessages;
};

export const defaultTimeMessages = defineMessages({
  monthsLeft: {
    id: 'TimeLeft.monthsLeft2',
    defaultMessage:
      '{months}m {days, plural, =0 {{hours, plural, =0 {{rawMinutes, plural, =0 {} other {{minutes}min}}} other {{hours}h}}} other {{days}d}}',
  },
  daysLeft: {
    id: 'TimeLeft.daysLeft2',
    defaultMessage:
      '{days}d {hours, plural, =0 {{minutes, plural, =0 {{seconds}s} other {{minutes}m}}} other {{hours}h}}',
  },
  hoursLeft: {
    id: 'TimeLeft.hoursLeft2',
    defaultMessage:
      '{hours}h {minutes, plural, =0 {{seconds}s} other {{minutes}m}}',
  },
  minutesLeft: {
    id: 'TimeLeft.minutesLeft2',
    defaultMessage: '{minutes}m {seconds}s',
  },
  secondsLeft: {
    id: 'TimeLeft.secondsLeft2',
    defaultMessage: '{minutes}m {seconds}s',
  },
});

const pad = (duration: number) => duration.toString().padStart(2, '0');

interface SplitOptions {
  withPadding?: boolean;
}

export const split = (
  duration: Duration,
  splitOptions?: SplitOptions
): TimeElements => {
  return {
    months: duration.months || 0,
    days: duration.days || 0,
    hours: duration.hours || 0,
    minutes:
      splitOptions?.withPadding === false
        ? String(duration.minutes || 0)
        : pad(duration.minutes || 0),
    seconds:
      splitOptions?.withPadding === false
        ? String(duration.seconds || 0)
        : pad(duration.seconds || 0),
    rawMinutes: duration.minutes || 0,
  };
};

export const formatDurationMessage = (
  duration: Duration,
  elements: TimeElements,
  messages: TimeMessages
) => {
  let message;
  if (elements.months > 0) {
    message = messages.monthsLeft;
  } else if (elements.days > 0) {
    message = messages.daysLeft;
  } else if (elements.hours > 0) {
    message = messages.hoursLeft;
  } else if ((duration.minutes || 0) > 0) {
    message = messages.minutesLeft;
  } else {
    message = messages.secondsLeft;
  }
  return message;
};
